/**
 * voice-persist — IndexedDB-backed audio cache for the chat composer.
 *
 * Founder spec 2026-05-20 (M-X feature C2): when the microphone stops
 * we MUST hold the audio bytes locally even if the upload / transcribe
 * call fails. localStorage is unsuitable (audio blobs blow past the
 * ~5 MB quota in seconds), so we use IndexedDB.
 *
 * Schema: a single object store `pending` keyed by an autogenerated id.
 * Each row: { id, cardId, mime, blob, mode, createdAt, lastError? }.
 *
 * Lifecycle:
 *   - stash(blob, …)  → returns the row id
 *   - drain(cardId?)  → list stashed rows (all, or filtered to a card).
 *                       The Voice Inputs sub-tab reads this to surface every
 *                       not-yet-uploaded recording with Retry + Download, so a
 *                       failed upload is never silent.
 *   - drop(id)        → remove ONLY after the server confirms a durable upload
 *   - markError(id)   → keep the row, annotate the last failure (never lost)
 *
 * Failure modes — all guarded, and all REPORTED rather than papered over:
 *   - No IndexedDB (Safari private browsing, hardened profiles): `stash()`
 *     returns `null`. It used to return a synthetic id, which made the caller
 *     believe a durable local copy existed when none did — the same
 *     "assert a state you never verified" bug that cost the founder trust
 *     three times this week. A `null` id means: there is NO safety net here,
 *     say so.
 *   - Quota errors: surfaced via the Promise rejection so the caller can tell
 *     the user the recording could not be cached.
 *
 * LIMITS — this is a safety net, not an archive:
 *   - Storage is best-effort unless `navigator.storage.persist()` is granted;
 *     browsers evict it under disk pressure.
 *   - Safari deletes all script-writable storage for a site that has not been
 *     visited for 7 days (ITP).
 *   - Private/incognito windows keep it only for the life of the window.
 *   Anything the founder must not lose has to reach the server.
 */

const DB_NAME = 'chaos.voice.v1';
const STORE = 'pending';

export type StashMode = 'submit' | 'paste';

export interface StashedAudio {
  id: string;
  cardId: string | null;
  mime: string;
  blob: Blob;
  mode: StashMode;
  createdAt: string;
  /** Recording length in ms, when known — surfaced by the Voice tab. */
  durationMs?: number;
  lastError?: string;
}

/**
 * Where a failure note lives — NOT in the audio row.
 *
 * `markError` used to `store.get(id)`, set `lastError`, and `store.put(row)`
 * the whole record back, which re-serialises the Blob through IndexedDB on
 * every failed upload. On 2026-08-15 that ran five times against a 28.1 MB
 * recording that existed nowhere else. Rewriting an irreplaceable blob to
 * record the fact that something went wrong is a bad trade at any probability:
 * the note is worth a few dozen bytes and the audio is worth the session.
 *
 * So: once the audio is written, ITS RECORD IS NEVER REWRITTEN. Notes go in
 * localStorage, which is tiny, synchronous, and cannot touch the blob store.
 * Losing a note is a cosmetic regression; losing the blob is the catastrophe
 * this module exists to prevent.
 */
const NOTE_PREFIX = 'chaos.voice.err.';

function readNote(id: string): string | undefined {
  try {
    return localStorage.getItem(NOTE_PREFIX + id) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeNote(id: string, reason: string): void {
  try {
    localStorage.setItem(NOTE_PREFIX + id, reason.slice(0, 500));
  } catch {
    /* quota / disabled storage — a missing note is never worth failing over */
  }
}

function clearNote(id: string): void {
  try {
    localStorage.removeItem(NOTE_PREFIX + id);
  } catch {
    /* ignore */
  }
}

function unsupported(): boolean {
  return typeof indexedDB === 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (unsupported()) {
      reject(new Error('indexeddb_unsupported'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexeddb_open_failed'));
  });
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `v-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  // Every call used to open a connection and never close one. The Voice tab
  // polls `drain()` every 15 s and on every window focus, so a long session
  // accumulated hundreds of live IDBDatabase handles against the store holding
  // the founder's audio. Closing is one line and removes a whole class of
  // "why is IndexedDB behaving strangely" from the picture.
  try {
    return await withStoreOnDb<T>(db, mode, fn);
  } finally {
    try {
      db.close();
    } catch {
      /* already closing */
    }
  }
}

function withStoreOnDb<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result: T;
    let resolved = false;
    Promise.resolve(fn(store))
      .then((v) => {
        result = v;
      })
      .catch((err) => {
        if (!resolved) {
          resolved = true;
          reject(err);
        }
      });
    tx.oncomplete = () => {
      if (!resolved) {
        resolved = true;
        resolve(result);
      }
    };
    tx.onerror = () => {
      if (!resolved) {
        resolved = true;
        reject(tx.error ?? new Error('indexeddb_tx_failed'));
      }
    };
    tx.onabort = () => {
      if (!resolved) {
        resolved = true;
        reject(tx.error ?? new Error('indexeddb_tx_aborted'));
      }
    };
  });
}

export const voicePersist = {
  isSupported(): boolean {
    return !unsupported();
  },

  /**
   * Ask the browser to make this origin's storage NON-EVICTABLE, and report
   * what it actually said.
   *
   * "SAVED ON THIS DEVICE" is a promise the browser can break. Without
   * `navigator.storage.persist()` an origin's IndexedDB is best-effort: it can
   * be evicted under disk pressure, and Safari's ITP clears script-writable
   * storage for a site not visited for 7 days. Nothing in this codebase had
   * ever asked. The founder read that sentence on 2026-08-15 and believed it,
   * and he was lucky it was true.
   *
   * Chromium grants this without a prompt for an installed / frequently-used
   * origin and silently declines otherwise; Firefox prompts; Safari does not
   * implement `persist()` at all. So the return value is not a formality — it
   * is the difference between a sentence the UI may say and one it may not.
   * Callers MUST render the `false` case honestly rather than assuming.
   */
  async requestPersistence(): Promise<boolean> {
    try {
      const s = navigator?.storage;
      if (!s) return false;
      if (typeof s.persisted === 'function' && (await s.persisted())) return true;
      if (typeof s.persist !== 'function') return false;
      return await s.persist();
    } catch {
      return false;
    }
  },

  /** Is this origin's storage actually protected from eviction right now? */
  async isPersisted(): Promise<boolean> {
    try {
      const s = navigator?.storage;
      if (!s || typeof s.persisted !== 'function') return false;
      return await s.persisted();
    } catch {
      return false;
    }
  },
  /**
   * Persist a recording locally before the upload is attempted.
   *
   * Returns the row id, or `null` when this browser has no IndexedDB — in
   * which case the caller MUST NOT pretend the audio is safe.
   */
  async stash(
    blob: Blob,
    opts: { cardId: string | null; mime: string; mode: StashMode; durationMs?: number },
  ): Promise<string | null> {
    if (unsupported()) return null;
    // Ask for eviction protection BEFORE the first recording lands, not after
    // one has been lost. Best-effort and never fatal: the answer is surfaced
    // by `isPersisted()` so the UI can be honest either way.
    void this.requestPersistence().catch(() => false);
    const id = uuid();
    const row: StashedAudio = {
      id,
      cardId: opts.cardId,
      mime: opts.mime,
      blob,
      mode: opts.mode,
      createdAt: new Date().toISOString(),
    };
    if (typeof opts.durationMs === 'number') row.durationMs = opts.durationMs;
    await withStore('readwrite', (store) => {
      return new Promise<void>((resolve, reject) => {
        const req = store.add(row);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error('indexeddb_add_failed'));
      });
    });
    return id;
  },
  /**
   * Remove a stashed recording.
   *
   * CALL THIS ONLY ON PROVEN DURABILITY or on an explicit human "discard".
   * `uploadRecordedAudio` gates it on the server's `durable: true`, which means
   * the bytes are fsynced under a committed name AND a row points at them —
   * not on a 2xx, not on an id in a response body.
   */
  async drop(id: string): Promise<void> {
    clearNote(id);
    if (unsupported()) return;
    await withStore('readwrite', (store) => {
      return new Promise<void>((resolve, reject) => {
        const req = store.delete(id);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error ?? new Error('indexeddb_delete_failed'));
      });
    });
  },
  /**
   * How much the safety net is currently holding. A stash row is dropped only
   * on a confirmed upload, so permanently-failed recordings accumulate — and a
   * full quota makes `stash()` start FAILING exactly when it is needed most.
   * Surface this so the founder can clear the backlog deliberately; never
   * auto-delete audio to make room.
   */
  async usage(): Promise<{ count: number; bytes: number }> {
    const rows = await this.drain().catch(() => [] as StashedAudio[]);
    return {
      count: rows.length,
      bytes: rows.reduce((n, r) => n + (r.blob?.size ?? 0), 0),
    };
  },
  async drain(cardId?: string): Promise<StashedAudio[]> {
    if (unsupported()) return [];
    return withStore('readonly', (store) => {
      return new Promise<StashedAudio[]>((resolve, reject) => {
        const req = store.getAll();
        req.onsuccess = () => {
          const all = (req.result ?? []) as StashedAudio[];
          // Failure notes live outside the audio record (see NOTE_PREFIX) and
          // are merged back on read, so the UI sees the same shape as before
          // while the blob is never rewritten.
          const rows = all.map((r) => {
            const note = readNote(r.id);
            return note ? { ...r, lastError: note } : r;
          });
          if (!cardId) {
            resolve(rows);
            return;
          }
          resolve(rows.filter((r) => r.cardId === cardId));
        };
        req.onerror = () => reject(req.error ?? new Error('indexeddb_getall_failed'));
      });
    });
  },
  /**
   * Annotate a stash with why its last upload failed — WITHOUT touching the
   * audio record. See NOTE_PREFIX for why that distinction is load-bearing.
   */
  async markError(id: string, reason: string): Promise<void> {
    writeNote(id, reason);
  },
};
